import { RootState } from "@/redux/store";
import { routes } from "@/routes/utils";
import { useGetTaskList } from "@/services";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { setTaskData } from "@/redux/slices";
import { getBaseRoute } from "@/lib/utils";
import { useLocation } from "react-router-dom";
import moment from "moment";
import { useIsMobile } from "@/hooks/use-mobile";

const useViewTaskPlan = () => {
  const {id, action, planId, planAction} = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isMobile = useIsMobile();
  const { projectData, planData } = useSelector(
    (state: RootState) => state.currentData
  );

  const basePath = getBaseRoute(useLocation().pathname);
  const [filterType, setFilterType] = useState<string>("month");

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);
  
  const breadcrumbData = useMemo(
    () => [
      {
        link: `/${basePath}/${routes.projectOverview}`,
        name: `Project Overview`,
      },
      {
        link: `/${basePath}/${routes.projectOverview}/${id}/${action}/${routes.projectPlan}`,
        name: `${projectData?.name} Project Details`
      },
      {
        link: `/${basePath}/${routes.projectOverview}/${id}/${action}/${routes.projectPlan}/${planId}/${planAction}`,
        name: `${planData?.name} Details`
      },
      {
        link: "",
        name: 'View Task Plan'
      },
    ],
    [projectData, planData]
  );

  const {data: taskList, isLoading: isLoadingTaskList } = useGetTaskList({
    projectId: id as string,
    planId: planId as string,
    queryParams: {
      enabled: !!(id && planId)
    }
  });

  const groupedTasks = useMemo(() => {
    let tasks = [];
    if (taskList?.data && Array.isArray(taskList.data)) {
      tasks = taskList.data;
    }
    
    const sortedTasks = [...tasks].sort((a: any, b: any) => {
      const d1 = a.plannedStartDate ? moment(a.plannedStartDate).valueOf() : 0;
      const d2 = b.plannedStartDate ? moment(b.plannedStartDate).valueOf() : 0;
      return d1 - d2;
    });

    const validDates = sortedTasks.map(t => moment(t.plannedStartDate)).filter(m => m.isValid());
    let minYear = moment().year();
    let maxYear = moment().year();
    if (validDates.length > 0) {
      minYear = Math.min(...validDates.map(d => d.year()));
      maxYear = Math.max(...validDates.map(d => d.year()));
    }

    const groupsMap = new Map<string, { labelDate: moment.Moment; displayLabel: string; tasks: any[] }>();

    if (filterType === "month") {
      for (let y = minYear; y <= maxYear; y++) {
        for (let m = 0; m < 12; m++) {
          const monthDate = moment({ year: y, month: m, day: 1 });
          const key = monthDate.format("MMM YYYY");
          let label = "No Date";
          const mm = monthDate.format('MMM');
          const yy = monthDate.format('YYYY');
          const map: Record<string, string> = { 'Mar': 'March', 'Apr': 'April' };
          label = `${map[mm] || mm} ${yy}`;
          groupsMap.set(key, { labelDate: monthDate, displayLabel: label, tasks: [] });
        }
      }
    } else {
      const startOfYear = moment({ year: minYear }).startOf('year');
      const endOfYear = moment({ year: maxYear }).endOf('year');
      let curr = startOfYear.clone().startOf('week');
      while (curr.isBefore(endOfYear)) {
        const key = curr.format("YYYY-ww");
        const displayLabel = `${curr.format("DD MMM")} - ${curr.clone().endOf('week').format("DD MMM YY")}`;
        groupsMap.set(key, { labelDate: curr.clone(), displayLabel, tasks: [] });
        curr.add(1, 'week');
      }
    }

    sortedTasks.forEach((task: any) => {
      const dateStr = task.plannedStartDate;
      const date = dateStr ? moment(dateStr) : null;
      const isValidDate = date && date.isValid();
      
      let key = "No Date";
      if (isValidDate) {
        if (filterType === "month") {
          key = date.format("MMM YYYY");
        } else {
          key = date.clone().startOf('week').format("YYYY-ww");
        }
      }
      
      if (!groupsMap.has(key)) {
        groupsMap.set(key, { 
          labelDate: isValidDate ? (filterType === 'month' ? date.clone().startOf('month') : date.clone().startOf('week')) : moment(0),
          displayLabel: isValidDate ? (filterType === 'month' ? date.format("MMM YYYY") : `${date.clone().startOf('week').format("DD MMM")} - ${date.clone().endOf('week').format("DD MMM YY")}`) : "No Date",
          tasks: [] 
        });
      }
      groupsMap.get(key)!.tasks.push(task);
    });

    return Array.from(groupsMap.values()).sort((a, b) => {
      return a.labelDate.valueOf() - b.labelDate.valueOf();
    }).map(g => {
      return {
        label: g.displayLabel,
        tasks: g.tasks
      };
    });
  }, [taskList?.data, filterType]);

  const taskSummary = useMemo(() => {
    const summary = {
      total: 0,
      completed: 0,
      inProgress: 0,
      onHold: 0,
      blocked: 0,
      notStarted: 0
    };
    if (taskList?.data && Array.isArray(taskList.data)) {
      summary.total = taskList.data.length;
      taskList.data.forEach((t: any) => {
        switch(t?.actualStatus) {
          case 'COMPLETED': summary.completed++; break;
          case 'IN_PROGRESS': summary.inProgress++; break;
          case 'ON_HOLD': summary.onHold++; break;
          case 'BLOCKED': summary.blocked++; break;
          case 'DRAFT': summary.notStarted++; break;
          default: summary.notStarted++; break;
        }
      });
    }
    return summary;
  }, [taskList?.data]);

  const onBackClick = () => {
    navigate(`/${basePath}/${routes.projectOverview}/${id}/${action}/${routes.projectPlan}/${planId}/${planAction}`)
  };


  const onTaskClick = (task: any) => {
    dispatch(setTaskData({...task, id: task.taskId}));
    navigate(`/${basePath}/${routes.projectOverview}/${id}/${action}/${routes.projectPlan}/${planId}/${planAction}`);
  };

  return {
    breadcrumbData,
    isLoadingTaskList,
    taskList: taskList?.data || [],
    groupedTasks,
    filterType,
    setFilterType,
    onBackClick,
    onTaskClick,
    taskSummary,
    isMobile,
  };
}

export default useViewTaskPlan;